import css from "./Header.sass?inline"
import {Icon} from "@/ui/components/Icon.tsx"
import {Lifecycle, panic, Terminator} from "@opendaw/lib-std"
import {TransportGroup} from "@/ui/header/TransportGroup.tsx"
import {TimeStateDisplay} from "@/ui/header/TimeStateDisplay.tsx"
import {createElement, Group, RouteLocation} from "@opendaw/lib-jsx"
import {StudioService} from "@/service/StudioService"
import {MenuButton} from "@/ui/components/MenuButton.tsx"
import {Button} from "@/ui/components/Button.tsx"
import {Colors, IconSymbol} from "@opendaw/studio-enums"
import {Html} from "@opendaw/lib-dom"
import {MenuItem, StudioPreferences} from "@opendaw/studio-core"
import {Manual, Manuals} from "@/ui/pages/Manuals"
import {HorizontalPeakMeter} from "@/ui/components/HorizontalPeakMeter"
import {gainToDb} from "@opendaw/lib-dsp"
import {EngineAddresses} from "@opendaw/studio-adapters"
import {UndoRedoButtons} from "@/ui/header/UndoRedoButtons"
import {MetronomeControl} from "@/ui/header/MetronomeControl"
import {PerformanceStats} from "@/ui/header/PerformanceStats"
import {BaseFrequencyControl} from "@/ui/header/BaseFrequencyControl"
import {MicSourceSelector} from "@/ui/header/MicSourceSelector"

const className = Html.adoptStyleSheet(css, "Header")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

export const Header = ({lifecycle, service}: Construct) => {
    const peaksInDb = new Float32Array(2)
    const runtime = lifecycle.own(new Terminator())
    lifecycle.own(service.projectProfileService.catchupAndSubscribe((optProfile) => {
        runtime.terminate()
        optProfile.match<unknown>({
            none: () => peaksInDb.fill(Number.NEGATIVE_INFINITY),
            some: ({project: {liveStreamReceiver}}) =>
                runtime.own(liveStreamReceiver
                    .subscribeFloats(EngineAddresses.PEAKS, ([l, r]) => {
                        peaksInDb[0] = gainToDb(l)
                        peaksInDb[1] = gainToDb(r)
                    }))
        })
    }))
    const addManualMenuItems = (manuals: ReadonlyArray<Manual>): ReadonlyArray<MenuItem> => manuals.map(manual => {
        if (manual.type === "page") {
            return MenuItem.default({
                label: manual.label,
                icon: manual.icon,
                checked: RouteLocation.get().path === manual.path,
                separatorBefore: manual.separatorBefore ?? false
            }).setTriggerProcedure(() => RouteLocation.get().navigateTo(manual.path))
        } else if (manual.type === "folder") {
            return MenuItem.default({
                label: manual.label,
                icon: manual.icon,
                separatorBefore: manual.separatorBefore ?? false
            }).setRuntimeChildrenProcedure(parent => parent.addMenuItem(...addManualMenuItems(manual.files)))
        } else {
            return panic()
        }
    })
    const {preferences} = service.engine
    const opslaanButton: HTMLElement = (
        <Button lifecycle={lifecycle}
                appearance={{color: Colors.cream, activeColor: Colors.bright}}
                onClick={() => service.saveToCloud()}>
            <h5>Opslaan</h5>
        </Button>
    )
    lifecycle.own(service.projectProfileService.catchupAndSubscribe(optProfile => {
        opslaanButton.classList.toggle("hidden", optProfile.isEmpty())
    }))
    return (
        <header className={className}>
            <MenuButton root={service.menu}
                        appearance={{color: Colors.gray, activeColor: Colors.bright, tinyTriangle: true}}>
                <h5>openDAW</h5>
            </MenuButton>
            <MenuButton root={MenuItem.root()
                .setRuntimeChildrenProcedure(parent =>
                    parent.addMenuItem(
                        MenuItem.header({label: "Manuals", icon: IconSymbol.OpenDAW, color: Colors.green}),
                        ...addManualMenuItems(Manuals)
                    ))} appearance={{color: Colors.green, tinyTriangle: true}}>
                <Icon symbol={IconSymbol.Help}/>
            </MenuButton>
            {opslaanButton}
            <hr/>
            <Group onInit={element => StudioPreferences.catchupAndSubscribe(enabled =>
                element.classList.toggle("hidden", !enabled), "visibility", "enable-history-buttons")}>
                <UndoRedoButtons lifecycle={lifecycle} service={service}/>
                <hr/>
            </Group>
            <TransportGroup lifecycle={lifecycle} service={service}/>
            <hr/>
            <TimeStateDisplay lifecycle={lifecycle} service={service}/>
            <BaseFrequencyControl lifecycle={lifecycle} service={service}/>
            <hr/>
            <MetronomeControl lifecycle={lifecycle}
                              service={service}
                              preferences={preferences}/>
            <hr/>
            <div style={{flex: "1 0 0"}}/>
            {
                location.origin.includes("dev.opendaw.studio")
                && (<h5 style={{color: Colors.cream.toString()}}>DEV VERSION (UNSTABLE)</h5>)}
            <div style={{flex: "2 0 0"}}/>
            <hr/>
            <MicSourceSelector lifecycle={lifecycle}/>
            <hr/>
            <HorizontalPeakMeter lifecycle={lifecycle} peaksInDb={peaksInDb} width="4em"/>
            <hr/>
            <PerformanceStats lifecycle={lifecycle} service={service}/>
        </header>
    )
}